import { inject, InjectionToken, Provider } from '@angular/core';
import type { CustomSvgIconName } from '@panaya/ui/p-icon/customize';
import { trimTrailingSlash } from '@panaya/utils';

export interface PIconConfig {
    /**
     * The path to where the app will hold SVG assets folder after it is built.
     * Meaning, in your dist/<your_app> folder, where will SVG icon assets will be located.
     * Registered SVG icons will be read from this folder.
     *
     * *When using the {@link injectPIconConfig} function to fetch the config,
     * this is guarenteed to return no trailing slash.*
     *
     * @default `assets/icons/svgs`
     */
    svgIconsOutputFolder: string;
    /**
     * The names of the custom SVG icons to register with p-icon.
     * These are names of icons the app adds on top of the default ones provided by the library.
     * They should correspond with SVG files in the output folder pointed by {@link svgIconsOutputFolder}.
     */
    customSvgIconNames: readonly CustomSvgIconName[];
}

const CONFIG = new InjectionToken<PIconConfig>('The configuration for the p-icon module.');

export function providePIconConfig(config: Partial<PIconConfig>): Provider {
    const normalizedConfig: Partial<PIconConfig> = {
        ...config,
        svgIconsOutputFolder: config.svgIconsOutputFolder && trimTrailingSlash(config.svgIconsOutputFolder)
    };

    return { provide: CONFIG, useValue: normalizedConfig };
}

export function injectPIconConfig(): PIconConfig {
    return {
        // Default config
        svgIconsOutputFolder: 'assets/icons/svgs',
        customSvgIconNames: [],
        // Override with custom
        ...inject(CONFIG, { optional: true })
    };
}
